<template>
	<div>
		<div v-if="itemsPerPageSelect || haveFilterOption" class="row my-2 mx-0">
			<!-- GLOBAL FILTER (search everywhere) -->
			<div class="col-sm-6 form-inline p-0" v-if="haveFilterOption">
				<template v-if="tableFilter">
					<label class="mr-2">{{ tableFilterData.label }}</label>
					<input
						class="form-control"
						type="text"
						:placeholder="tableFilterData.placeholder"
						@input="tableFilterChange($event.target.value, 'input')"
						@change="tableFilterChange($event.target.value, 'change')"
						:value="tableFilterState"
						aria-label="table filter input" />
				</template>
				<!-- @ clean filter -->
				<slot name="cleaner" :clean="clear()" :isFiltered="isFiltered">
					<template v-if="cleaner">
						<!-- <CIcon-->
						<!-- v-if="cleaner"-->
						<!-- v-bind="cleanerProps"-->
						<!-- @click.native="clean"-->
						<!--/>-->
						Cleaner
					</template>
				</slot>
			</div>

			<!-- ITEMS PER PAGE SELECTBOX -->
			<div v-if="itemsPerPageSelect" class="col-sm-6 p-0" :class="{ 'offset-sm-6': !haveFilterOption }">
				<div class="form-inline justify-content-sm-end">
					<label class="mr-2">{{ paginationSelect.label }}</label>
					<select
						class="form-control"
						@change="paginationChange"
						aria-label="changes number of visible items">
						<option value="" selected disabled hidden>
							{{ perPageItems }}
						</option>
						<option v-for="(number, key) in paginationSelect.values" :val="number" :key="key">
							{{ number }}
						</option>
					</select>
				</div>
			</div>
		</div>

		<slot name="over-table" />
		<!-- Button to clear current search memory -->
		<CButton v-if="isFiltered" class="mb-3 ml-3 float-right btn-outline-danger" @click="clear">
			Réinitialiser les filtres
		</CButton>
		<CDropdown
			v-if="downloadable && internItems.length > 0"
			:disabled="isDownloading"
			color="outline-primary"
			:toggler-text="
				isDownloading
					? 'Téléchargement en cours...'
					: isFiltered
					? 'Exporter les donnés (filtré)'
					: 'Exporter les donnés'
			"
			class="mb-3 float-right">
			<CDropdownItem @click="downloadData('csv')">CSV</CDropdownItem>
			<CDropdownItem @click="downloadData('xml')">XML</CDropdownItem>
		</CDropdown>
		<div :class="`position-relative ${responsive ? 'table-responsive' : ''}`">
			<table :class="tableClasses">
				<thead>
					<slot name="thead-top" />

					<!-- COLUMNS SORTING [A-Z] - [Z-A] -->
					<tr v-if="header">
						<template v-for="(name, index) in columnNames">
							<th
								@click="changeSort(rawColumnNames[index], index)"
								:class="[headerClass(index), sortingIconStyles]"
								:style="headerStyles(index)"
								:key="index">
								<slot :name="`${rawColumnNames[index]}-header`">
									<div>{{ name }}</div>
								</slot>
								<slot
									v-if="isSortable(index)"
									name="sorting-icon"
									:state="getIconState(index)"
									:classes="iconClasses(index)">
									<fa-icon icon="arrow-up" :class="iconClasses(index)" />
								</slot>
							</th>
						</template>
					</tr>

					<!-- COLUMNS FILTERS -->
					<tr v-if="columnFilter" class="table-sm">
						<template v-for="(colName, index) in rawColumnNames">
							<th :class="headerClass(index)" :key="index">
								<slot :name="`${rawColumnNames[index]}-filter`">
									<!-- @ select filter -->
									<template v-if="fields[index].type === 'select'">
										<select
											v-if="!fields || fields[index].filter !== false"
											class="form-control form-control-sm"
											@change="
												columnFilterEvent(colName, $event.target.value, 'change')
											">
											<option></option>
											<!-- Placeholder -->
											<option
												v-for="option in fields[index].filterSelectOptions"
												:value="option.value"
												:selected="columnFilterState[colName] == option.value">
												{{ option.label }}
											</option>
										</select>
									</template>

									<!-- @ daterange -->
									<template
										v-else-if="
											fields[index].type === 'daterange' ||
											fields[index].type === 'date' ||
											fields[index].type === 'datetime'
										">
										<vc-date-picker
											is-range
											:model-config="modelConfig"
											mode="date"
											timezone="Europe/Paris"
											:masks="{ input: 'DD/MM/YY' }"
											:update-on-input="false"
											:value="columnFilterState[colName]"
											@input="columnFilterEvent(colName, $event, 'change')"
											:popover="{
												positionFixed: true,
											}"
											autocomplete="off">
											<template v-slot="{ inputValue, inputEvents, isDragging }">
												<input
													type="text"
													name="daterange"
													class="form-control form-control-sm"
													:value="
														inputValue.start
															? inputValue.start + ' au ' + inputValue.end
															: ''
													"
													v-on="inputEvents.start"
													autocomplete="off" />
											</template>
										</vc-date-picker>
									</template>

									<template v-else-if="fields[index].type === 'numberRange'">
										<div class="d-flex">
											<input
												placeholder="de"
												type="text"
												name="numberRange"
												class="form-control form-control-sm number-range-input"
												@change="numberRangeInput(colName, $event.target.value)"
												:value="getNumberRangeGt(colName)" />
											<input
												placeholder="à"
												type="text"
												name="numberRange"
												class="form-control form-control-sm number-range-input"
												@change="
													numberRangeInput(colName, $event.target.value, false)
												"
												:value="getNumberRangeLt(colName)" />
										</div>
									</template>

									<!-- @ checkboxes -->
									<template v-else-if="fields[index].type === 'checkboxes'">
										<input
											class="select-all-chkbx"
											v-model="internSelectAll"
											type="checkbox"
											@change="selectAllItems()" />
									</template>

									<!-- @ gtreeview -->
									<template v-else-if="fields[index].type === 'autocomplete'">
										<GTreeView
											:instance-id="index"
											@update:selected="
												data => columnFilterEvent(colName, data, 'change')
											"
											@input="data => onInputAutocomplete(colName, data)"
											placeholder="Rechercher.."
											:multiple="false"
											:default-options="true"
											:load-options="loadAutocomplete"
											:async="true"
											:value="columnFilterState[colName] || null" />
									</template>

									<!-- @ (default) input filter -->
									<template v-else>
										<input
											v-if="!fields || fields[index].filter !== false"
											class="form-control form-control-sm"
											@input="columnFilterEvent(colName, $event.target.value, 'input')"
											@change="
												columnFilterEvent(colName, $event.target.value, 'change')
											"
											:value="columnFilterState[colName]"
											:aria-label="`column name: '${colName}' filter input`" />
									</template>
								</slot>
							</th>
						</template>
					</tr>
				</thead>

				<!-- THE TABLE BODY -->
				<tbody :style="clickableRows ? 'cursor:pointer;' : null" class="position-relative">
					<!-- <td> ITEMS TEMPLATE -->
					<template v-for="(item, itemIndex) in currentItems">
						<tr
							@click="rowClicked(item, itemIndex + firstItemIndex, $event)"
							:class="item._classes"
							:tabindex="clickableRows ? 0 : null"
							:key="itemIndex">
							<template v-for="(colName, index) in rawColumnNames">
								<!-- @ Show scoped element if it's exists -->
								<slot
									v-if="$scopedSlots[colName]"
									:name="colName"
									:item="item"
									:index="itemIndex + firstItemIndex" />

								<!-- @ Show checkbox select if its needed -->
								<td v-else-if="colName === 'checkboxes'" :key="index">
									<input type="checkbox" v-model="checkboxes" :value="item['@id']" />
								</td>

								<!-- @ Show french date if it's needed -->
								<td v-else-if="fields[index].type === 'date'" :key="index">
									<template v-if="get(item, colName)">
										{{ $moment(get(item, colName), 'YYYY-MM-DD').format('DD/MM/YYYY') }}
									</template>
								</td>

								<!-- @ Show french datetime if it's needed -->
								<td v-else-if="fields[index].type === 'datetime'" :key="index">
									<template v-if="get(item, colName)">
										{{
											$moment(get(item, colName), 'YYYY-MM-DD HH:mm').format(
												'DD/MM/YYYY HH:mm'
											)
										}}
									</template>
								</td>

								<!-- @ Show french datetime if it's needed -->
								<td v-else-if="fields[index].type === 'daterange'" :key="index">
									<template v-if="get(item, colName)">
										{{ $moment(get(item, colName)).format('DD/MM/YYYY') }}
									</template>
								</td>

								<td v-else :class="cellClass(item, colName, index)" :key="index">
									<!-- @ Basic item -->
									<template
										v-if="
											colName.indexOf('.') === -1 && Boolean(item[colName]) !== false
										">
										<!-- @@ check if item is not undefined -->
										{{ String(item[colName]) }}
									</template>

									<!-- @ Nested item (with dots) -->
									<template v-if="colName.indexOf('.') !== -1">
										{{ nestedPathResolve(item, colName) }}
									</template>
								</td>
							</template>
						</tr>

						<!-- @ Show details if there is a details scope -->
						<!-- the details is shown as a button and when we click on it, there is more elements that are appearing -->
						<tr
							v-if="$scopedSlots.details"
							@click="rowClicked(item, itemIndex + firstItemIndex, $event, true)"
							class="p-0"
							style="border: none !important"
							:key="'details' + itemIndex">
							<td :colspan="colspan" class="p-0" style="border: none !important">
								<slot name="details" :item="item" :index="itemIndex + firstItemIndex" />
							</td>
						</tr>
					</template>

					<!-- @ If there is no items -->
					<tr v-if="!currentItems.length">
						<td :colspan="colspan">
							<slot name="no-items-view">
								<div class="text-center my-5">
									<h2>
										{{ noItemsText }}
										<fa-icon icon="ban" style="color: red" />
									</h2>
								</div>
							</slot>
						</td>
					</tr>
				</tbody>

				<!-- FOOTER -->
				<tfoot v-if="footer && currentItems.length > 0">
					<tr>
						<template v-for="(name, index) in columnNames">
							<th
								@click="changeSort(rawColumnNames[index], index)"
								:class="[headerClass(index), sortingIconStyles]"
								:style="headerStyles(index)"
								:key="index">
								<slot :name="`${rawColumnNames[index]}-header`">
									<div>{{ name }}</div>
								</slot>
								<slot
									v-if="isSortable(index)"
									name="sorting-icon"
									:state="getIconState(index)">
									<fa-icon icon="arrow-up" :class="iconClasses(index)" />
								</slot>
							</th>
						</template>
					</tr>
				</tfoot>
				<slot name="footer" :itemsAmount="currentItems.length" />
				<slot name="caption" />
			</table>

			<!-- IS LOADING ELEMENT -->
			<slot name="loading" v-if="internLoading">
				<GElementCover
					:boundaries="[
						{ sides: ['top'], query: 'td' },
						{ sides: ['bottom'], query: 'tbody' },
					]" />
			</slot>
		</div>

		<!-- UNDER-TABLE SLOT -->
		<slot name="under-table" />

		<!-- PAGINATION -->
		<GPagination
			v-if="pagination"
			v-show="totalPages > 1"
			:activePage.sync="page"
			:pages="totalPages"
			v-bind="typeof pagination === 'object' ? pagination : null" />

		<div class="text-center">Il y a {{ totalQueryItemsCount }} résultat(s).</div>

		<template v-if="checkboxFieldName">
			<template v-if="checkboxes.length > 0">
				<template v-for="checkbox in checkboxes">
					<input type="hidden" :value="checkbox" :name="checkboxFieldName" />
				</template>
			</template>
			<template v-else>
				<input type="hidden" :value="null" :name="checkboxFieldName" />
			</template>
		</template>
	</div>
</template>

<script>
	import { find, get, map, uniqBy } from 'lodash';
	import GElementCover from './GElementCover';
	import GPagination from './GPagination';
	import GTreeView from './GTreeView';
	import mercure from './libs/mercure';

	export default {
		name: 'GDataTableRemote',

		components: {
			GTreeView,
			GPagination,
			GElementCover,
		},

		props: {
			remoteUrl: null,
			items: Array,
			fields: Array,
			itemsPerPage: {
				type: Number,
				default: 10,
			},
			activePage: Number,
			pagination: [Boolean, Object],
			addTableClasses: [String, Array, Object],
			responsive: {
				type: Boolean,
				default: true,
			},
			size: String,
			dark: Boolean,
			striped: Boolean,
			fixed: Boolean,
			hover: Boolean,
			border: Boolean,
			outlined: Boolean,
			itemsPerPageSelect: [Boolean, Object],
			sorter: [Boolean, Object],
			tableFilter: [Boolean, Object],
			defaultFilterState: Object,
			columnFilter: [Boolean, Object],
			lazy: {
				// Need to press 'enter' or cursor click out of focus to validate column search
				type: Boolean,
				default: true,
			},
			sorterValue: {
				type: Object,
				default: () => {
					return {};
				},
			},
			tableFilterValue: String,
			columnFilterValue: Object,
			header: {
				type: Boolean,
				default: true,
			},
			footer: Boolean,
			loading: Boolean,
			clickableRows: Boolean,
			noItemsView: Object,
			cleaner: Boolean,
			mercureUrl: String,
			preSelectedItems: {
				/** If some items should be pre-selected */ type: Array,
				default: () => [],
			},
			downloadable: {
				type: Boolean,
				default: true, // Show the export button by default
			},
		},

		data() {
			return {
				get: get,
				tableFilterState: this.tableFilterValue,
				columnFilterState: {},
				sorterState: {
					column: null,
					asc: true,
				},
				modelConfig: {
					start: {
						timeAdjust: '00:00:00',
					},
					end: {
						timeAdjust: '00:00:00',
					},
				},
				range: {
					start: new Date(2020, 0, 6),
					end: new Date(2020, 0, 23),
				},
				numberRange: {
					gt: null,
					lt: null,
				},
				page: 0,
				perPageItems: this.itemsPerPage,
				internItems: this.items || [],
				internLoading: this.loading || false,
				internSelectAll: false,
				totalPages: 0,
				checkboxes: [],
				totalQueryItemsCount: 0,
				isDownloading: false,
				downloadSearchQueries: null,
				tableKey: this.getTableKey(),
			};
		},

		mounted() {
			// set page info if it exists in the query
			this.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1;

			// Assign default value to filters
			if (this.defaultFilterState) this.columnFilterState = this.defaultFilterState;

			// Load the filters from localStorage
			this.applyTableFilters();

			if (this.mercureUrl != null) mercure.watch(this, this.mercureUrl, this.refresh, false);
		},

		watch: {
			/** On page change */
			page() {
				// Check page number is not out of range
				if (this.page > this.totalPages && this.totalPages != 0) this.page = this.totalPages;
				if (this.page < 1) this.page = 1;
				this.refresh();
			},

			itemsPerPage(val) {
				this.perPageItems = val;
			},

			/**  Watch any change of the path */
			$route: {
				handler() {
					this.applyTableFilters();
				},
			},

			/** On sort */
			sorterValue: {
				immediate: true,
				handler(val) {
					const asc = val.asc === false ? false : true;
					this.sorterState = Object.assign({}, { asc, column: val.column });
				},
			},

			tableFilterValue(val) {
				this.tableFilterState = val;
			},

			columnFilterValue: {
				immediate: true,
				handler(val) {
					this.columnFilterState = Object.assign({}, val);
				},
			},

			items(val, oldVal) {
				if (val && oldVal && this.objectsAreIdentical(val, oldVal)) {
					return;
				}
				this.internItems = val || [];
			},

			totalPages: {
				immediate: true,
				handler(val) {
					this.$emit('pages-change', val);
				},
			},

			computedPage(val) {
				this.$emit('page-change', val);
			},

			/**
			 * Set the checkboxes with the @id (id url) of pre-selected items.
			 * The array should contains a @id column or should have only a one unique column of @id strings
			 * */
			preSelectedItems: {
				immediate: true,
				handler(val) {
					if (this.preSelectedItems.length > 0 && this.preSelectedItems[0]['@id'] == null) {
						// Le tableau ne contient pas de valeur "@id", on renvoi alors le tableau en supposant qu'il n'y a que des ids
						this.checkboxes = this.preSelectedItems;
					} else if (this.preSelectedItems.length > 0) {
						this.checkboxes = map(this.preSelectedItems, '@id');
					}
				},
			},

			checkboxes: function () {
				this.$emit('update:checkboxes', this.checkboxes);
			},

			/** clear datatable on new obj creation */
			fields: function () {
				this.clear();
			},

			/** clear datatable on new obj creation */
			remoteUrl: function () {
				this.clear();
			},
		},

		computed: {
			checkboxFieldName() {
				let searchCheckboxesField = find(this.fields, { key: 'checkboxes' });
				if (searchCheckboxesField == null) {
					return null;
				}

				return searchCheckboxesField.fieldName;
			},

			columnFiltered() {
				return this.internItems;
			},

			itemsDataColumns() {
				return this.rawColumnNames.filter(name => {
					return this.generatedColumnNames.includes(name);
				});
			},

			firstItemIndex() {
				return (this.computedPage - 1) * this.perPageItems || 0;
			},

			paginatedItems() {
				return this.sortedItems;
			},

			currentItems() {
				return this.internItems;
			},

			computedPage() {
				return this.pagination ? this.page : this.activePage;
			},

			generatedColumnNames() {
				function* getPropertyNames(obj, prefix = '') {
					for (const key in obj) {
						if (Object.prototype.hasOwnProperty.call(obj, key)) {
							const fullPath = prefix ? `${prefix}.${key}` : key;
							if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
								yield* getPropertyNames(obj[key], fullPath);
							} else {
								yield fullPath;
							}
						}
					}
				}
				return [...getPropertyNames(this.internItems[0] || {})].filter(el => el.charAt(0) !== '_');
			},

			rawColumnNames() {
				if (this.fields) {
					return this.fields.map(el => el.key || el);
				}
				return this.generatedColumnNames;
			},

			columnNames() {
				if (this.fields) {
					return this.fields.map(f => {
						return f.label !== undefined ? f.label : this.pretifyName(f.key || f);
					});
				}
				return this.rawColumnNames.map(el => this.pretifyName(el));
			},

			tableClasses() {
				return [
					'table',
					this.addTableClasses,
					{
						[`table-${this.size}`]: this.size,
						'table-dark': this.dark,
						'table-striped': this.striped,
						'table-fixed': this.fixed,
						'table-hover': this.hover,
						'table-bordered': this.border,
						border: this.outlined,
					},
				];
			},

			sortingIconStyles() {
				return { 'position-relative pr-4': this.sorter };
			},

			colspan() {
				return this.rawColumnNames.length;
			},

			tableFilterData() {
				return {
					label: this.tableFilter.label || 'Filter:',
					placeholder: this.tableFilter.placeholder || 'type string...',
				};
			},

			paginationSelect() {
				return {
					label: this.itemsPerPageSelect.label || 'Items per page:',
					values: this.itemsPerPageSelect.values || [5, 10, 20, 50],
				};
			},

			noItemsText() {
				const customValues = this.noItemsView || {};
				if (this.internItems.length) {
					return customValues.noResults || 'No filtering results';
				}
				return customValues.noItems || 'No items';
			},

			isFiltered() {
				return (
					this.tableFilterState ||
					Object.values(this.columnFilterState).join('') ||
					this.sorterState.column
				);
			},

			cleanerProps() {
				return {
					content: this.$options.icons.cilFilterX,
					class: `ml-2 ${this.isFiltered ? 'text-danger' : 'transparent'}`,
					role: this.isFiltered ? 'button' : null,
					tabindex: this.isFiltered ? 0 : null,
				};
			},

			haveFilterOption() {
				return this.tableFilter || this.cleaner || this.$scopedSlots.cleaner;
			},
		},

		methods: {
			refresh() {
				this.internLoading = true;

				let searchQueries = new URLSearchParams();
				searchQueries.append('page', this.page);
				searchQueries.append('itemsPerPage', this.perPageItems);

				// Columns search filters
				Object.entries(this.columnFilterState).forEach(([key, value]) => {
					const columnParams = find(this.fields, { key: key });
					const columnFilter = String(value).toLowerCase();

					// --> basic string search
					if (
						columnFilter &&
						this.rawColumnNames.includes(key) &&
						!key.includes('search_') &&
						columnParams.type !== 'daterange' &&
						columnParams.type !== 'datetime' &&
						columnParams.type !== 'date' &&
						columnParams.type !== 'numberRange'
					) {
						// remove [0] from key
						key = key.replace(/\[[0-9]\]/, '');

						searchQueries.append(key, columnFilter);

						// --> advanced string search
					} else if (columnFilter && this.rawColumnNames.includes(key) && key.includes('search_')) {
						const searchTerms = columnFilter.split(' ');
						searchTerms.forEach(term => {
							searchQueries.append(key + '[]', term);
						});

						// --> date range search
					} else if (
						this.rawColumnNames.includes(key) &&
						value !== null &&
						(columnParams.type === 'daterange' ||
							columnParams.type === 'datetime' ||
							columnParams.type === 'date')
					) {
						searchQueries.append(
							`${key}[after]`,
							this.$moment(value.start).format('YYYY-MM-DD') + 'T00:00:00.000Z'
						);
						searchQueries.append(
							`${key}[before]`,
							this.$moment(value.end).format('YYYY-MM-DD' + 'T23:59:59.999Z')
						);

						// --> number range search
					} else if (
						this.rawColumnNames.includes(key) &&
						value !== null &&
						columnParams.type === 'numberRange'
					) {
						if (value?.gt !== null) {
							searchQueries.append(`${key}[gt]`, value.gt);
						} else if (this.numberRange.gt != null && this.numberRange.gt.length > 0) {
							searchQueries.append(`${key}[gt]`, this.numberRange.gt);
						}

						if (value?.lt !== null) {
							searchQueries.append(`${key}[lt]`, value.lt);
						} else if (this.numberRange.lt != null && this.numberRange.lt.length > 0) {
							searchQueries.append(`${key}[lt]`, this.numberRange.lt);
						}
					}
				});

				// Columns orderBy
				searchQueries.append(
					['order[', this.sorterState.column, ']'].join(''),
					this.sorterState.asc ? 'asc' : 'desc'
				);

				// Save all filters in localStorage
				localStorage.setItem(
					'colikadoTabFilters',
					JSON.stringify({
						...this.getTableFilters(),
						[this.tableKey]: {
							columnFilterState: this.columnFilterState,
							sorterState: this.sorterState,
							page: this.page,
						},
					})
				);

				// Save search queries for download
				this.downloadSearchQueries = new URLSearchParams(searchQueries);

				this.$axios.get(this.remoteUrl, { params: searchQueries }).then(res => {
					this.internItems = res.data['hydra:member'];
					this.totalPages = Math.ceil(parseInt(res.data['hydra:totalItems']) / this.perPageItems); // Math.ceil = round-up
					this.totalQueryItemsCount = res.data['hydra:totalItems'];
					this.internLoading = false;
				});

				this.$emit('calculateWeightAndVolume', new URLSearchParams(searchQueries));
			},

			nestedPathResolve(o, s, startSubstr = 0) {
				var output = [];

				s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
				s = s.replace(/^\./, ''); // strip a leading dot
				var a = s.split('.');
				for (var i = startSubstr, n = a.length; i < n; ++i) {
					var k = a[i];

					// --> Basic one item search
					if (o === Object(o) && k in o) {
						o = o[k];

						// --> Recursive search
					} else if (o.length > 0) {
						for (let $key in o) {
							if (k in o[$key]) {
								output.push(this.nestedPathResolve(o[$key][k], s, i + 1));
							}
						}
						break;

						// --> Return undefined if key cannot be found
					} else {
						return;
					}
				}

				// Push only if there was not any recursive search
				if (output.length === 0) {
					output.push(o);
				}

				if (output.length === 1) {
					// Single object to output
					return output.join('');
				} else {
					// Multiple objects to output
					return output.join(', ');
				}
			},

			dateFormat(dateString, from, to) {
				const date = this.$moment(dateString, from).format(to);

				if (date === 'Invalid date') {
					return dateString;
				}

				return date;
			},

			selectAllItems() {
				if (this.internSelectAll) {
					this.internLoading = true;

					let searchQueries = new URLSearchParams();
					searchQueries.append('itemsPerPage', '9999');
					searchQueries.append('properties', 'id');

					this.$axios.get(this.remoteUrl, { params: searchQueries }).then(res => {
						this.checkboxes = _.map(res.data['hydra:member'], '@id');
						this.internLoading = false;
					});
				} else {
					this.checkboxes = [];
				}
			},

			changeSort(column, index) {
				if (!this.isSortable(index)) {
					return;
				}
				//if column changed or sort was descending change asc to true
				const state = this.sorterState;
				const columnRepeated = state.column === column;
				if (!this.sorter || !this.sorter.resetable) {
					state.column = column;
				} else {
					state.column = columnRepeated && state.asc === false ? null : column;
				}
				state.asc = !(columnRepeated && state.asc);
				this.$emit('update:sorter-value', this.sorterState);
				this.refresh();
			},

			columnFilterEvent(colName, value, type) {
				const isLazy = this.lazy;
				if ((isLazy && type === 'input') || (!isLazy && type === 'change')) {
					return;
				}
				this.$set(this.columnFilterState, colName, value);
				this.$emit('update:column-filter-value', this.columnFilterState);
				this.refresh();
			},

			/** Generate a unique key using the path */
			getTableKey() {
				return (
					'cpTableKey_' +
					window.location.href
						.replace(window.location.protocol + '/', '')
						.replace(window.location.hostname + '/', '')
						.replace(/\?page=\d+/, '') // Excludes ?page={number}
				);
			},

			/** Get saved filters from localStorage */
			getTableFilters() {
				try {
					const parsedFilter = JSON.parse(localStorage.getItem('colikadoTabFilters'));
					return parsedFilter;
				} catch (err) {
					console.log(err);
					return false;
				}
			},

			/** Apply saved filters from localStorage */
			applyTableFilters() {
				const filters = this.getTableFilters();
				if (!filters) return;

				if (filters[this.tableKey]) {
					this.columnFilterState = filters[this.tableKey]?.columnFilterState || {};
					this.sorterState = {
						column: filters[this.tableKey]?.sorterState?.column || null,
						asc: filters[this.tableKey]?.sorterState?.asc || true,
					};
					const filterPage = filters[this.tableKey]?.page || 1;
					if (filterPage !== this.page) this.page = filterPage;
				}
			},

			tableFilterChange(value, type) {
				const isLazy = this.tableFilter && this.tableFilter.lazy === true;
				if ((isLazy && type === 'input') || (!isLazy && type === 'change')) {
					return;
				}
				this.tableFilterState = value;
				this.$emit('update:table-filter-value', this.tableFilterState);
			},

			pretifyName(name) {
				return name
					.replace(/[-_.]/g, ' ')
					.replace(/ +/g, ' ')
					.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
					.split(' ')
					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
					.join(' ');
			},

			cellClass(item, colName, index) {
				let classes = [];
				if (item._cellClasses && item._cellClasses[colName]) {
					classes.push(item._cellClasses[colName]);
				}
				if (this.fields && this.fields[index]._classes) {
					classes.push(this.fields[index]._classes);
				}
				return classes;
			},

			isSortable(index) {
				return (
					this.sorter &&
					(!this.fields || this.fields[index].sorter !== false) &&
					this.itemsDataColumns.includes(this.rawColumnNames[index])
				);
			},

			headerClass(index) {
				const fields = this.fields;
				return fields && fields[index]._classes ? fields[index]._classes : '';
			},

			headerStyles(index) {
				let style = 'vertical-align:middle;overflow:hidden;';
				if (this.isSortable(index)) {
					style += `cursor:pointer;`;
				}
				if (this.fields && this.fields[index] && this.fields[index]._style) {
					style += this.fields[index]._style;
				}
				return style;
			},

			rowClicked(item, index, e, detailsClick = false) {
				this.$emit('row-clicked', item, index, this.getClickedColumnName(e, detailsClick), e);
			},

			getClickedColumnName(e, detailsClick) {
				if (detailsClick) {
					return 'details';
				} else {
					const children = Array.from(e.target.closest('tr').children);
					const clickedCell = children.filter(child => child.contains(e.target))[0];
					return this.rawColumnNames[children.indexOf(clickedCell)];
				}
			},

			getIconState(index) {
				const direction = this.sorterState.asc ? 'asc' : 'desc';
				return this.rawColumnNames[index] === this.sorterState.column ? direction : 0;
			},

			iconClasses(index) {
				const state = this.getIconState(index);
				return [
					'icon-transition position-absolute arrow-position',
					{
						transparent: !state,
						'rotate-icon': state === 'desc',
					},
				];
			},

			paginationChange(e) {
				this.$emit('pagination-change', Number(e.target.value));
				if (this.itemsPerPageSelect.external) {
					return;
				}
				this.perPageItems = Number(e.target.value);
			},

			objectsAreIdentical(obj1, obj2) {
				return obj1.length === obj2.length && JSON.stringify(obj1) === JSON.stringify(obj2);
			},

			loadAutocomplete({ action, searchQuery, instanceId, callback }) {
				let field = this.fields[instanceId];
				let labelFieldName = field.labelFieldName ? field.labelFieldName : 'name';
				let searchFieldName = field.searchFieldName ? field.searchFieldName : 'id';

				if (action === 'ASYNC_SEARCH') {
					let params = new URLSearchParams();
					params.append(labelFieldName, searchQuery);
					params.append('itemsPerPage', 100);

					let baseUrl = field.queryUrl.includes('?') ? field.queryUrl + '&' : field.queryUrl + '?';

					this.$axios.get(`${baseUrl}${params.toString()}`).then(res => {
						let options = res.data['hydra:member'];
						options = uniqBy(options, o => {
							return o[labelFieldName];
						});

						options = options.map(item => {
							return {
								'@id': item[searchFieldName],
								name: item[labelFieldName],
							};
						});

						callback(null, options);
					});
				}
			},

			onInputAutocomplete(colName, data) {
				if (data == null || data.length === 0) {
					this.columnFilterEvent(colName, '', 'change');
				}
			},

			/** clear datatable to default state */
			clear() {
				this.tableFilterState = '';
				this.columnFilterState = {};
				this.sorterState = { column: '', asc: true };
				this.internSelectAll = false;
				this.page = 1;

				this.refresh();
			},

			/** Download data in csv or xml format */
			async downloadData(type) {
				if (this.internItems.length === 0) return; // No data to download
				if (this.totalQueryItemsCount >= 1000)
					if (
						!window.confirm(
							`Attention !
								Vous êtes sur le point de télécharger ${this.totalQueryItemsCount} lignes. Voulez-vous continuer ?`
						)
					)
						// If there is more than 1000 items, ask user to confirm download
						return;

				const LOTS = 5;
				const NB_ITEMS = 250;
				const NB_PAGES = Math.ceil(parseInt(this.totalQueryItemsCount) / NB_ITEMS);
				const SUB_GROUP_OF_REQUEST = Math.ceil(parseInt(NB_PAGES) / LOTS);
				const IGNORED_COLUMNS = ['checkboxes', 'actions', 'action', 'groupedActions'];

				this.isDownloading = true;

				// Circular replacer to avoid circular reference error in JSON.stringify
				const getCircularReplacer = () => {
					const seen = new WeakSet();
					return (key, value) => {
						// If value is an object or array, set it in the seen WeakSet to avoid circular reference
						if (typeof value === 'object' && value !== null) {
							if (seen.has(value)) return;
							seen.add(value);
						}

						// Search text and src (for image) values in scoped slots
						return String(key).search(/.*(?:context).*/gi) === -1 &&
							(key === 'text' || key === 'src' || typeof value === 'object')
							? value
							: null;
					};
				};

				// Recursive function to concat text values in scoped slots
				const concatText = obj =>
					obj === null || obj === undefined || typeof obj !== 'object'
						? ''
						: Object.entries(obj).reduce(
								(acc, [key, value]) =>
									key === 'text' || key === 'src' ? acc + value : acc + concatText(value),
								''
						  );

				// Reset pagination
				this.downloadSearchQueries.delete('itemsPerPage');
				this.downloadSearchQueries.append('itemsPerPage', String(NB_ITEMS));

				// Construct all search queries for each page and sequence them by group of X requests
				const requests = [];
				let counter = 0;

				for (let i = 1; i <= SUB_GROUP_OF_REQUEST; i++) {
					const subRequest = [];
					for (let j = 1; j <= LOTS; j++) {
						counter++;
						if (counter > NB_PAGES) break;
						else {
							const searchQueries = new URLSearchParams(this.downloadSearchQueries);
							searchQueries.delete('page');
							searchQueries.append('page', String(counter));
							subRequest.push(searchQueries);
						}
					}
					requests.push(subRequest);
				}

				// Fetch sub request function
				const fetchSubRequest = async requests =>
					await Promise.all(
						requests.map(async searchQueries => {
							try {
								const result = await this.$axios.get(this.remoteUrl, {
									params: searchQueries,
								});

								if (result.status !== 200 || result.data['hydra:member'].length === 0)
									throw new Error();
								else return result.data['hydra:member'];
							} catch (error) {
								this.$toast.error(
									"Une erreur est survenue lors du téléchargement d'une partie des données, veuillez réessayer."
								);
								return [];
							}
						})
					);

				try {
					// Promise all requests in a queue by group of 3 asynchronously requests to optimize the performance
					const result = [];
					for (const subRequest of requests) {
						const resultSubRequest = await fetchSubRequest(subRequest);
						result.push(...resultSubRequest);
					}

					// Then Merge all results and check if there is data downloaded
					const mergedResult =
						result.length <= 0 ? [] : result.reduce((acc, val) => acc.concat(val), []);
					if (mergedResult.length <= 0) {
						this.$toast.error('Aucune donnée télécharger');
						this.isDownloading = false;
						return;
					}

					// Define first row as header
					let row = {};
					this.rawColumnNames.forEach((colName, index) => {
						// if is checkbox or actions ignore
						if (IGNORED_COLUMNS.includes(colName)) return;
						row[colName] = this.columnNames[index];
					});
					const header = row;

					// Define data
					const data = [
						header,
						...mergedResult.map(item => {
							let row = {};
							this.rawColumnNames.forEach((colName, index) => {
								// if is checkbox or actions ignore
								if (IGNORED_COLUMNS.includes(colName)) return;

								// if is date or datetime format
								if (get(item, colName)) {
									const field = this.fields[index].type;
									const value = get(item, colName);

									switch (field) {
										case 'date':
											row[colName] = this.$moment(value, 'YYYY-MM-DD').format(
												'DD/MM/YYYY'
											);
											return;

										case 'datetime':
											row[colName] = this.$moment(value, 'YYYY-MM-DD HH:mm').format(
												'DD/MM/YYYY HH:mm'
											);
											return;

										case 'daterange':
											row[colName] = this.$moment(value).format('DD/MM/YYYY');
											return;
									}
								}

								// if is scoped slot use text value
								if (this.$scopedSlots[colName] && typeof item[colName] !== 'boolean') {
									let searchedValue;
									const slot = this.$scopedSlots[colName]({ item });

									// Clean slot value and concat text
									searchedValue = concatText(
										JSON.parse(JSON.stringify(slot, getCircularReplacer()))
									);

									row[colName] =
										searchedValue !== undefined || searchedValue !== null
											? searchedValue
											: '';
								} else {
									// Otherwise use default value
									const nested = this.nestedPathResolve(item, colName);
									row[colName] =
										colName.indexOf('.') !== -1 ? nested : String(item[colName]);
								}
							});
							return row;
						}),
					];

					const filename = 'export_' + Date.now() + '.' + type;
					const separator = type === 'csv' ? ';' : ',';
					const mimeType = type === 'csv' ? 'text/csv' : 'application/' + type;

					const prepareData =
						mimeType === 'text/csv'
							? this.$dataExporter.jsonToCSV(data, separator)
							: this.$dataExporter.jsonToXML(data);

					const link = document.createElement('a');
					link.style.display = 'none';
					link.setAttribute(
						'href',
						'data:' + mimeType + ';charset=utf-8,' + encodeURIComponent(prepareData)
					);
					link.setAttribute('download', filename);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					this.isDownloading = false;
				} catch (e) {
					console.log(e);
					this.$toast.error('Une erreur est survenue !');
					this.isDownloading = false;
					return;
				}
			},

			numberRangeInput(colName, value, gt = true) {
				gt ? (this.numberRange.gt = value) : (this.numberRange.lt = value);
				this.columnFilterEvent(
					colName,
					{
						gt: gt ? value : this.columnFilterState[colName]?.gt ?? this.numberRange.gt,
						lt: gt ? this.columnFilterState[colName]?.lt ?? this.numberRange.lt : value,
					},
					''
				);
			},

			getNumberRangeGt(colName) {
				return this.columnFilterState[colName]?.gt ?? this.numberRange.gt;
			},

			getNumberRangeLt(colName) {
				return this.columnFilterState[colName]?.lt ?? this.numberRange.lt;
			},
		},
	};
</script>

<style scoped>
	thead tr:not(:last-child) th {
		border-bottom: 1px;
	}

	.transparent {
		opacity: 0.4;
	}
	.icon-transition {
		-webkit-transition: transform 0.3s;
		transition: transform 0.3s;
	}
	.arrow-position {
		right: 0;
		top: 50%;
		-ms-transform: translateY(-50%);
		transform: translateY(-50%);
	}
	.rotate-icon {
		-ms-transform: translateY(-50%) rotate(-180deg);
		transform: translateY(-50%) rotate(-180deg);
	}

	.select-all-chkbx {
		margin-left: 7px;
		margin-bottom: 6px;
	}

	.number-range-input {
		min-width: 60px;
	}
</style>
